
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, MailCheck, AlertCircle } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ResetPasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ResetPasswordDialog = ({ 
  isOpen, 
  onClose
}: ResetPasswordDialogProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form state when the dialog is closed
  useEffect(() => {
    if (!isOpen) {
      // Reset form on close
      setTimeout(() => {
        setEmail('');
        setIsSuccess(false);
        setError(null);
      }, 300); // Small delay to ensure animation completes
    }
  }, [isOpen]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setError(null);
    setIsSubmitting(true);
    
    try {
      // Check rate limit first
      const { data: ipData, error: ipError } = await supabase.functions.invoke('check-rate-limit', {
        body: { action: 'reset-password' }
      });
      
      if (ipError || (ipData && ipData.limited)) {
        throw new Error(ipData?.message || 'Too many attempts. Please try again later.');
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });
      
      if (error) throw error;
      
      setIsSuccess(true);
    } catch (err: any) {
      console.error('Error resetting password:', err.message);
      setError(err.message || 'An error occurred while resetting your password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // For the success screen, add a "Try different email" button
  const handleTryDifferentEmail = () => {
    setIsSuccess(false);
    setEmail('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-gray-800 text-white border border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-center text-xl flex items-center justify-center gap-2">
            {isSuccess ? (
              <>
                <MailCheck className="h-5 w-5 text-green-400" />
                Check Your Email
              </>
            ) : (
              <>
                <Mail className="h-5 w-5 text-blue-400" />
                Reset Password
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-center text-gray-300">
            {isSuccess 
              ? `We've sent a password reset link to ${email}`
              : 'Enter your email address and we\'ll send you a link to reset your password.'}
          </DialogDescription>
        </DialogHeader>
        
        {isSuccess ? (
          <div className="flex flex-col items-center space-y-4 py-4">
            <div className="text-sm text-gray-300 text-center space-y-2">
              <p>Please check your inbox and click the link to reset your password.</p>
              <p>If you don't see the email, please check your spam folder.</p>
            </div>
            
            <div className="flex flex-col w-full gap-3">
              <Button 
                onClick={handleTryDifferentEmail}
                variant="outline"
                className="border-gray-600 hover:bg-gray-700 text-white"
              >
                Try Different Email
              </Button>
              
              <Button 
                onClick={onClose}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive" className="bg-red-900/50 border-red-800">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {error}
                </AlertDescription>
              </Alert>
            )}
            
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              required
            />
            
            <div className="flex justify-center">
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 w-full"
              >
                {isSubmitting ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
